import React, { Fragment, useLayoutEffect, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Avatar, Switch } from "antd";
import { Menu, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useHostStore } from "host/HostStore";
import { useNavigate } from "react-router-dom";
import { EnvironmentType, useGetEnvironmentsForProductLazyQuery } from "store/graphql/codegen/auto-gen/environment";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { useKeyValueSearchParams } from "shared/hooks";
import SideBarContext from "store/context/SideBarContext/SideBarContext";
import TSCookiesManager from "store/storage/cookies/cookies";
import {
    getShowPrimarySideBarFromLocalStorage,
    logoutUser,
    setShowPrimarySideBarInLocalStorage,
    setShowSecondarySideBarInLocalStorage,
    getShowSecondarySideBarFromLocalStorage
} from "store/storage/localstorage/localstorage";
import {
    getRawShowPrimarySideBarFromSearchParams,
    getShowPrimarySideBarFromSearchParams,
    setShowPrimarySideBarOnSearchParams,
    setShowSecondarySideBarOnSearchParams,
    getShowSecondarySideBarFromSearchParams
} from "utils/searchParams";
import { BrandPlatform, BrandPlatformLogo, BrandPlatformName } from "components/BrandPlatform";
import { IAuthState } from "models/auth.model";
import InvitePeers from "components/Shared/UI/Invite/InvitePeers";
import { ProductSelectionWrapper } from "components/ProductSelection/ProductSelection";
import { EnvironmentSelectionWrapper } from "components/EnvironmentSelection/EnvironmentSelection";
import { Logo } from "components/Logo/Logo";
import { UserContextData } from "ts-utils-lib";
import Cookies from "js-cookie";
import axios from "axios";
import { hostClient, httpLinkConfigurator } from "store/graphql/apollo/clients";
import { useGetProductsForUserIdLazyQuery, useGetUserLazyQuery } from "store/graphql/codegen/auto-gen/user";
import { Environment } from "models/environment.model";
import { UserOutlined } from "@ant-design/icons";
import { switchToken } from "./header.helpers";
import { PricingTile } from "components/PricingTile/PricingTile";
import WidgetSearchBox from "components/SearchWidgets";

const DEFAULT_SIDEBAR_OPEN_STATUS = false;

type Props = {
    auth: IAuthState;
};

export type ProductDetails = {
    accountId: string;
    productId: string;
    productName: string;
    orgId: string;
    orgName: string;
};

const DEMO_PRODUCT_ID = "f01334c6-f726-11ee-bd2a-e60358d08e04";

function getCookie(name: string) {
    const matches = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)")
    );
    return matches ? decodeURIComponent(matches[1]) : undefined;
}

export const Header = ({ auth }: Props) => {
    const navigate = useNavigate();
    const [appTheme, setAppTheme] = useState("light");
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [userName, setUserName] = useState(UserContextData.GetUserName());

    const [searchParams, setSearchParams] = useSearchParams();

    const [isDemoSelected, setIsDemoSelected] = useState(() => {
        return localStorage.getItem("isDemoProductSelected") === "true";
    });

    const [searchKeyValueParams, setKeyValueSearchParams] = useKeyValueSearchParams();
    const sideBarCtx = React.useContext(SideBarContext);
    const showPrimaryMenu = getShowPrimarySideBarFromSearchParams(searchKeyValueParams);
    const { setAuth, setProductEnvironments, setIsSystemTokenSet } = useHostStore();
    let currentProductId = UserContextData.GetProductId();
    let currentAccountId = localStorage.getItem("accountId") || "";
    const [skippedSteps, setSkippedSteps] = useState(false);
    const avatarInitWord = localStorage.getItem("userName")?.charAt(0).toUpperCase();
    const [products, setProducts] = useState<ProductDetails[]>([
        {
            accountId: currentAccountId,
            productId: currentProductId,
            productName: UserContextData.GetProductName(),
            orgId: UserContextData.GetOrganizationId(),
            orgName: UserContextData.GetOrganizationName()
        }
    ]);

    const [getProductsForUserIdLazyQuery] = useGetProductsForUserIdLazyQuery({
        client: hostClient,
        fetchPolicy: "no-cache"
    });

    const [getUser] = useGetUserLazyQuery({
        client: hostClient,
        fetchPolicy: "no-cache"
    });

    const updateDemoMode = (isDemoMode: boolean) => {
        setIsDemoSelected(isDemoMode);
        localStorage.setItem("isDemoProductSelected", isDemoMode.toString());

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("demoMode", isDemoMode.toString());
        setSearchParams(newSearchParams);

        const event = new CustomEvent("demoToggleFromHeader", {
            detail: { message: isDemoMode }
        });
        window.dispatchEvent(event);

        const demoToggleEvent = new CustomEvent("demoToggle", {
            detail: { message: isDemoMode }
        });
        window.dispatchEvent(demoToggleEvent);
    };

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === "isDemoProductSelected") {
                const newValue = event.newValue === "true";
                setIsDemoSelected(newValue);

                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set("demoMode", newValue.toString());
                setSearchParams(newSearchParams);
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        const storedDemoMode = localStorage.getItem("isDemoProductSelected") === "true";
        const urlDemoMode = searchParams.get("demoMode") === "true";

        if (urlDemoMode !== storedDemoMode) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set("demoMode", storedDemoMode.toString());
            setSearchParams(newSearchParams);
        }
    }, []);

    useEffect(() => {
        const urlDemoMode = searchParams.get("demoMode");
        if (urlDemoMode !== null) {
            const demoModeFromUrl = urlDemoMode === "true";
            const storedDemoMode = localStorage.getItem("isDemoProductSelected") === "true";

            if (demoModeFromUrl !== storedDemoMode) {
                updateDemoMode(demoModeFromUrl);

                if (products.length > 0) {
                    handleSelectDemoProductId(demoModeFromUrl);
                }
            }
        }
    }, [searchParams, products]);

    const onTogglePrimarySideBarMenu = () => {
        sideBarCtx.setShowPrimaryMenu(!sideBarCtx.showPrimaryMenu);
        setShowPrimarySideBarOnSearchParams(searchParams, setKeyValueSearchParams, !sideBarCtx.showPrimaryMenu);
        setShowPrimarySideBarInLocalStorage(!sideBarCtx.showPrimaryMenu);
        setShowSecondarySideBarInLocalStorage(!getShowSecondarySideBarFromLocalStorage);
        setShowSecondarySideBarOnSearchParams(
            searchParams,
            setKeyValueSearchParams,
            !getShowSecondarySideBarFromSearchParams(searchKeyValueParams)
        );
    };

    const profileMenuRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const rawShowPrimarySideBarQueryParam = getRawShowPrimarySideBarFromSearchParams(searchKeyValueParams);
        const primarySideBarStatusFromLocalStorage = getShowPrimarySideBarFromLocalStorage();

        if (typeof rawShowPrimarySideBarQueryParam !== "string") {
            const status =
                typeof primarySideBarStatusFromLocalStorage === "boolean"
                    ? primarySideBarStatusFromLocalStorage
                    : DEFAULT_SIDEBAR_OPEN_STATUS;
            sideBarCtx.setShowPrimaryMenu(status);
            return;
        }

        if (showPrimaryMenu !== sideBarCtx.showPrimaryMenu) {
            sideBarCtx.setShowPrimaryMenu(showPrimaryMenu);
        }
    }, [showPrimaryMenu, searchKeyValueParams]);

    const onLogoutHandler = () => {
        logoutUser();
        TSCookiesManager.logoutUser();
        setAuth(null);
        window.location.reload();
    };

    useEffect(() => {
        const timerId = setInterval(() => {
            const currentSystemToken = getCookie("system_token");
            if (!currentSystemToken) {
                onLogoutHandler();
            }
        }, 600000);

        return () => clearInterval(timerId);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setIsProfileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [profileMenuRef]);

    const handleSelectDemoProductId = (isDemoSelected: boolean) => {
        if (isDemoSelected) {
            const demoProduct = products.find((product) => product.productId === DEMO_PRODUCT_ID);
            if (demoProduct) handleProductChange(demoProduct);
        } else {
            const product = products.find((product) => product.productId !== DEMO_PRODUCT_ID);
            if (product) handleProductChange(product);
        }
    };
    const trackDemoModeUsed = () => {
        if (window.thriveStack && typeof window.thriveStack.track === "function") {
            window.thriveStack.track([
                {
                    event_name: "feature_used",
                    properties: {
                        feature_name: "Demo mode used"
                    },
                    user_id: localStorage.getItem("userDetails"),
                    timestamp: new Date().toISOString(),
                    context: {
                        group_id: localStorage.getItem("accountId")
                    }
                }
            ]);
        } else {
            console.log("ThriveStack not found");
        }
    };

    const handleChangeDemoMode = () => {
        const newDemoMode = !isDemoSelected;
        updateDemoMode(newDemoMode);
        handleSelectDemoProductId(newDemoMode);
        trackDemoModeUsed();
    };

    useEffect(() => {
        const handleCustomEvent = (event: Event) => {
            const customEvent = event as CustomEvent<any>;
            const newDemoMode = customEvent.detail.message;

            if (newDemoMode !== isDemoSelected) {
                updateDemoMode(newDemoMode);
            }
        };

        window.addEventListener("demoToggle", handleCustomEvent as EventListener);
        return () => {
            window.removeEventListener("demoToggle", handleCustomEvent);
        };
    }, [isDemoSelected]);

    useEffect(() => {
        const handleDemoSelected = () => {
            updateDemoMode(true);
            handleSelectDemoProductId(true);
        };

        window.removeEventListener("select_demo_mode", handleDemoSelected);
        window.addEventListener("select_demo_mode", handleDemoSelected);

        return () => {
            window.removeEventListener("select_demo_mode", handleDemoSelected);
        };
    }, [products]);

    const GetSystemToken = async (productId: string, orgId: string) => {
        const userId = UserContextData.GetUserId();
        const { data } = await axios.post(
            `${process.env.API_URL}/v1/GetSystemToken`,
            {
                user_id: userId,
                product_id: productId,
                organizationId: orgId
            },
            {
                headers: {
                    authorization: `Bearer ${Cookies.get("system_token")}`,
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE"
                }
            }
        );
        return data.token;
    };

    const handleToggleOnProductChange = (product: {
        productId: any;
        productName?: string;
        orgId?: string;
        orgName?: string;
    }) => {
        const newDemoMode = product?.productId === DEMO_PRODUCT_ID;
        updateDemoMode(newDemoMode);
    };

    const [getEnvironmentsForProduct] = useGetEnvironmentsForProductLazyQuery({
        client: httpLinkConfigurator,
        fetchPolicy: "no-cache"
    });

    const fetchEnvironments = (productId: string, callback?: () => void) => {
        getEnvironmentsForProduct({
            variables: {
                input: {
                    productId: productId
                }
            },
            onCompleted: (data) => {
                console.log(data);
                if (data?.getEnvironmentsForProduct?.environments) {
                    const envs = data?.getEnvironmentsForProduct?.environments.map((env) => ({
                        id: env.id,
                        name: env.name,
                        displayName: env.displayName,
                        environmentType:
                            env.environmentType == EnvironmentType.NonProduction ? "Non Production" : "Production"
                    }));
                    setProductEnvironments(envs as Environment[]);
                }
                if (callback) {
                    callback();
                }
            },
            onError: (error) => {
                console.log(error);
            }
        });
    };

    const reload = () => {
        window.location.reload();
    };

    const handleProductChange = async (product: ProductDetails) => {
        if (product?.productId !== currentProductId) {
            handleToggleOnProductChange(product);
            UserContextData.SetProductId(product.productId);
            UserContextData.SetProductName(product.productName);
            UserContextData.SetOrganizationId(product.orgId);
            UserContextData.SetOrganizationName(product.orgName);
            localStorage.setItem("productName", product.productName);
            localStorage.setItem("productId", product.productId);
            localStorage.setItem("organizationId", product.orgId);
            localStorage.setItem("organizationName", product.orgName);
            localStorage.setItem("accountId", product.accountId);
            const event = new Event("update_product_name");
            window.dispatchEvent(event);
            try {
                await switchToken();

                const tokenResponse = await GetSystemToken(product.productId, product.orgId);
                if (tokenResponse) {
                    UserContextData.SetUserTokenWithDomain(tokenResponse, `.${process.env.DOMAIN_NAME}`);
                } else {
                    console.error("Failed to retrieve token");
                }

                fetchEnvironments(product.productId, reload);
            } catch (error) {
                console.error("Error during token switch or fetching environments:", error);
            }
            fetchEnvironments(product.productId, reload);
        }
    };

    useEffect(() => {
        const fetchSystemToken = async (productId: string, orgId: string) => {
            try {
                const tokenResponse = await GetSystemToken(productId, orgId);
                if (tokenResponse) {
                    UserContextData.SetUserTokenWithDomain(tokenResponse, `.${process.env.DOMAIN_NAME}`);
                    setIsSystemTokenSet(true);
                    window.dispatchEvent(new Event("systemTokenSet"));
                } else {
                    console.error("Failed to retrieve token");
                }
            } catch (error) {
                console.error("Error retrieving token:", error);
            }
        };

        if (products && products[0]) {
            fetchSystemToken(products[0].productId, products[0].orgId);
        }
    }, [products]);

    useEffect(() => {
        const userId = UserContextData.GetUserId();
        if (!userId) {
            console.error("No userDetails found in localStorage");
            return;
        }

        const fetchData = async () => {
            getProductsForUserIdLazyQuery({
                variables: {
                    userId: userId
                },
                onCompleted: (data: any) => {
                    if (data?.getProductsForUserId?.products.length > 0) {
                        const getSortPriority = (product: ProductDetails) => {
                            if (product.productId === currentProductId) return 1;
                            if (product.productId === DEMO_PRODUCT_ID) return -1;
                            return 0;
                        };

                        const sortedProducts = data.getProductsForUserId.products.sort(
                            (a: ProductDetails, b: ProductDetails) => {
                                return getSortPriority(b) - getSortPriority(a);
                            }
                        );
                        setProducts(sortedProducts);

                        UserContextData.SetOrganizationId(sortedProducts[0]?.orgId);
                        UserContextData.SetProductId(sortedProducts[0]?.productId);
                        UserContextData.SetProductName(sortedProducts[0]?.productName);
                        localStorage.setItem("accountId", sortedProducts[0]?.accountId);

                        window.dispatchEvent(new Event("update_product_name"));

                        fetchEnvironments(sortedProducts[0].productId);
                    }
                },
                onError: (error: any) => {
                    console.log(error);
                }
            });
        };

        fetchData();
    }, []);

    useEffect(() => {
        console.log("products", products);
    }, [products]);

    useEffect(() => {
        getUser({
            variables: {
                userId: UserContextData.GetUserId()
            },
            onCompleted: (data) => {
                if (data?.getUser?.name) {
                    const userName = data.getUser.name;
                    UserContextData.SetUserName(userName);
                    setUserName(userName);
                }
            },
            onError: (error) => {
                console.log("error fetch user details", error);
            }
        });
    }, []);

    window.addEventListener("demoModeToggled", () => {
        setSkippedSteps(true);
    });

    const handleNavigation = (url: string, widget: any): void => {
        console.log("Navigating to:", url, widget);

        window.location.href = url;

        // You can also add analytics tracking here
        console.log("Widget clicked:", widget.title);
    };

    return (
        <div>
            <div
                className={`sticky top-0 z-40 flex flex-col flex-shrink-0 items-center justify-center gap-x-4 bg-white px-2 shadow-sm sm:gap-x-6 dark:bg-black dark:text-white ${
                    isDemoSelected ? "border-b-2 border-amber-800 h-16" : "border-b border-gray-200 h-14"
                }`}>
                {/* Separator */}
                <div className="h-6 w-px bg-gray-900/10 lg:hidden" aria-hidden="true" />
                <div className="flex grow gap-x-4 self-stretch lg:gap-x-6 items-center">
                    <div className="relative flex items-center gap-x-2">
                        <Bars3Icon className="h-6 w-6 cursor-pointer" onClick={() => onTogglePrimarySideBarMenu()} />
                        <BrandPlatform>
                            {/* <BrandPlatformLogo />
                            <BrandPlatformName>ThriveStack</BrandPlatformName> */}
                            <div>
                                <Logo />
                            </div>
                        </BrandPlatform>
                    </div>
                    <ProductSelectionWrapper handleClick={handleProductChange} products={products} />
                    <EnvironmentSelectionWrapper />
                    <WidgetSearchBox onNavigate={handleNavigation} />
                    {/* Demo Mode */}
                    <div className="flex grow items-center justify-end gap-x-4 lg:gap-x-6">
                        {/* <PricingTile /> */}
                        {!isDemoSelected ? <PricingTile /> : null}

                        <div className="flex gap-4 items-center">
                            <Switch
                                autoFocus={true}
                                disabled={false}
                                key={2}
                                onChange={handleChangeDemoMode}
                                checked={isDemoSelected}
                                style={{ backgroundColor: isDemoSelected ? "#0525f5" : "gray" }}
                            />
                            <span className="text-sm font-medium text-[#334155]">Demo Mode</span>
                        </div>
                        <InvitePeers />

                        {/* Separator */}
                        <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" aria-hidden="true" />

                        {/* Profile dropdown */}
                        <Menu as="div" className="relative" ref={profileMenuRef}>
                            <Menu.Button
                                className="-m-1.5 flex items-center p-1.5"
                                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}>
                                <span className="sr-only">Open user menu</span>
                                {avatarInitWord ? (
                                    <Avatar style={{ backgroundColor: "#273BE2" }}>{avatarInitWord}</Avatar>
                                ) : (
                                    <Avatar style={{ backgroundColor: "#273BE2" }} icon={<UserOutlined />} />
                                )}
                                {/* <span className="hidden lg:flex lg:items-center">
                                    <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                                </span> */}
                            </Menu.Button>
                            <Transition
                                show={isProfileMenuOpen}
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95">
                                <Menu.Items className="absolute right-0 z-10 mt-2.5 w-64 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                                    <div className="px-4 py-3 flex justify-between items-center bg-gray-100 cursor-pointer">
                                        <div className="flex justify-between gap-4 items-center">
                                            {avatarInitWord ? (
                                                <Avatar style={{ backgroundColor: "#273BE2" }}>{avatarInitWord}</Avatar>
                                            ) : (
                                                <Avatar
                                                    style={{ backgroundColor: "#273BE2" }}
                                                    icon={<UserOutlined />}
                                                />
                                            )}
                                            <div>
                                                <p className="text-sm">{userName}</p>
                                                <p className="truncate text-sm font-medium text-gray-900">
                                                    {auth?.email}
                                                </p>
                                            </div>
                                        </div>
                                        {/* <Cog6ToothIcon className="w-4 h-4" /> */}
                                    </div>
                                    <Menu.Item>
                                        <div
                                            className="flex items-center py-2 cursor-pointer hover:bg-gray-50"
                                            onClick={onLogoutHandler}
                                            style={{ zIndex: 1000 }}>
                                            <div className="block px-3 py-1 text-sm leading-6 text-gray-600">
                                                Logout
                                            </div>
                                        </div>
                                    </Menu.Item>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>
                </div>

                {isDemoSelected && (
                    <div className="absolute bottom-0 bg-amber-50 text-amber-800 font-bold text-lg py-2 px-6 border border-gray-50">
                        Demo Product
                    </div>
                )}
            </div>
        </div>
    );
};
